// Native share abstraction — Web Share API when available, fallback to channel-specific URLs.
// Mobile-portable per ADR-0009: same interface will work in Expo via expo-sharing later.
import { config } from "./config";
import { createLogger } from "./logger";

const log = createLogger("share");

export const buildShareText = (property) => {
  const price = new Intl.NumberFormat("es-MX", {
    style: "currency", currency: "MXN", maximumFractionDigits: 0,
  }).format(property.price || 0);
  const op = property.operation === "renta" ? "En renta" : "En venta";
  return `🏡 ${property.title}\n${op} · ${price}\n📍 ${property.city}, ${property.state}`;
};

export const buildShareUrl = (property, source = "share") => {
  const origin = window.location.origin;
  const utm = `?utm_source=${source}&utm_medium=share`;
  return `${origin}/propiedades/${property.id}${utm}`;
};

// URL para compartir en redes sociales (Facebook / WhatsApp / Twitter).
// Apunta al endpoint HTML del backend que expone OG tags dinámicas con la
// foto principal de la propiedad. Los crawlers leen las meta antes del
// redirect; los usuarios humanos son redirigidos al detalle público.
export const buildSocialShareUrl = (property, source = "share") => {
  const origin = window.location.origin;
  const utm = `?utm_source=${source}&utm_medium=share`;
  return `${origin}/api/share/property/${property.id}${utm}`;
};

export const shareViaWhatsApp = (property) => {
  const url = buildSocialShareUrl(property, "whatsapp");
  const text = `${buildShareText(property)}\n\n${url}`;
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  log.info("shared:whatsapp", property.id);
};

export const shareViaFacebook = (property) => {
  const url = buildSocialShareUrl(property, "facebook");
  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank", "noopener,noreferrer,width=600,height=500");
  log.info("shared:facebook", property.id);
};

export const copyLinkToClipboard = async (property) => {
  // El link copiado casi siempre termina pegado en WhatsApp/Facebook/Messenger,
  // así que apuntamos al endpoint OG para que la preview muestre la foto real.
  const url = buildSocialShareUrl(property, "copy");
  try {
    await navigator.clipboard.writeText(url);
    log.info("shared:copy", property.id);
    return true;
  } catch (e) {
    log.warn("clipboard failed", e?.message);
    return false;
  }
};

export const shareViaNative = async (property) => {
  if (!navigator.share) return false;
  try {
    await navigator.share({
      title: property.title,
      text: buildShareText(property),
      // URL enviada al share sheet nativo (iOS/Android): apunta al endpoint OG
      // para que WhatsApp / Messages / Mail muestren la foto real.
      url: buildSocialShareUrl(property, "native"),
    });
    log.info("shared:native", property.id);
    return true;
  } catch (e) {
    if (e?.name !== "AbortError") log.warn("native share failed", e?.message);
    return false;
  }
};

export const canShareNatively = () => typeof navigator !== "undefined" && !!navigator.share;

export const contactPhoneUrl = (phone) => phone ? `tel:${phone.replace(/[^0-9+]/g, "")}` : null;
export const contactWhatsAppUrl = (phone, message) => {
  if (!phone) return null;
  const clean = phone.replace(/[^0-9]/g, "");
  return `https://wa.me/${clean}?text=${encodeURIComponent(message || "")}`;
};
export const contactEmailUrl = (email, subject, body) => {
  if (!email) return null;
  const params = new URLSearchParams();
  if (subject) params.set("subject", subject);
  if (body) params.set("body", body);
  return `mailto:${email}?${params.toString()}`;
};

export const brand = config;
